/**
 * Reading the co-host list, and the refusals that come back with it.
 *
 * The three host endpoints answer through `TrackerResult`, which resolves on
 * failure rather than throwing — a 404 means "you are not a host on this trip"
 * (never "that group is missing", so the gate cannot be used to confirm which
 * ids exist), and a 403 means a real host attempted an owner-only action. Both
 * arrive as `{ ok: false }`, so a card that only rendered `.data` would show an
 * empty host list and no explanation for either.
 */
import type { TrackerFailure, TrackerHost, TrackerResult } from '../services/tracker-api-client';

/** Adding and removing hosts is owner-only, server-side. */
export function isOwner(viewerRole: string | null | undefined): boolean {
  return String(viewerRole ?? '').trim().toLowerCase() === 'owner';
}

/**
 * Newest last, so a host added a minute ago appears where the eye is.
 *
 * Sorted by name rather than by id: the list is read to answer "who else can
 * see the guest list", and an ordering nobody can predict makes that question
 * take longer than it should. A host with no display name sorts by their email,
 * which is what the row shows in that case.
 */
export function sortHosts(hosts: ReadonlyArray<TrackerHost>): TrackerHost[] {
  return [...hosts].sort((a, b) =>
    (a.displayName || a.email || '').localeCompare(b.displayName || b.email || '', undefined, {
      sensitivity: 'base',
    }),
  );
}

const FAILURE_MESSAGES: Record<TrackerFailure['kind'], string> = {
  unauthorized: 'Your session expired. Sign in again to manage hosts.',
  // 404 is the gate's answer for "not a host", not for a missing group.
  not_host: 'You are not a host on this trip, so its hosts are not yours to see.',
  forbidden: 'Only the trip’s owner can add or remove hosts.',
  invalid: 'That email was not accepted.',
  conflict: 'That change conflicts with the current host list.',
  error: 'Could not load the hosts.',
};

/**
 * The first real failure among several results, in words a host can act on.
 *
 * The server's own sentence wins where it sent one — it knows why an email was
 * refused and this module does not. The table above is the fallback, and exists
 * because `not_host` and `unauthorized` carry no message at all.
 */
export function hostFailureMessage(
  results: ReadonlyArray<TrackerResult<unknown> | undefined>,
): string {
  for (const result of results) {
    if (!result || result.ok) continue;
    const { failure } = result;
    const server = 'message' in failure ? String(failure.message ?? '').trim() : '';
    return server || FAILURE_MESSAGES[failure.kind] || FAILURE_MESSAGES.error;
  }
  return '';
}
